import { db } from '../firebase';
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  increment,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where
} from 'firebase/firestore';

// Admin session flag — skip tracking for admin users
export const setAdminSession = (isAdmin: boolean) => {
  if (isAdmin) {
    sessionStorage.setItem('is_admin_session', 'true');
  } else {
    sessionStorage.removeItem('is_admin_session');
  }
};

const isAdminSession = (): boolean => {
  return sessionStorage.getItem('is_admin_session') === 'true';
};

// Get date keys for different time periods
const getDateKey = () => {
  const now = new Date();
  return {
    day: now.toISOString().split('T')[0], // YYYY-MM-DD
    week: getWeekKey(now),
    month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`, // YYYY-MM
    year: String(now.getFullYear())
  };
};

const getWeekKey = (date: Date) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  return `${date.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
};

// Track a page visit
export const trackVisit = async () => {
  if (isAdminSession()) return;
  const dates = getDateKey();
  const visitorId = getOrCreateVisitorId();

  try {
    // Update daily stats
    const dayRef = doc(db, 'stats', 'daily', 'days', dates.day);
    await setDoc(dayRef, {
      visits: increment(1),
      date: dates.day
    }, { merge: true });

    // Update weekly stats
    const weekRef = doc(db, 'stats', 'weekly', 'weeks', dates.week);
    await setDoc(weekRef, {
      visits: increment(1),
      week: dates.week
    }, { merge: true });

    // Update monthly stats
    const monthRef = doc(db, 'stats', 'monthly', 'months', dates.month);
    await setDoc(monthRef, {
      visits: increment(1),
      month: dates.month
    }, { merge: true });

    // Update total stats
    const totalRef = doc(db, 'stats', 'totals');
    await setDoc(totalRef, {
      totalVisits: increment(1)
    }, { merge: true });

    // Track unique visitors
    await trackUniqueVisitor(visitorId, dates);
  } catch (error) {
    console.error('Error tracking visit:', error);
  }
};

// Track unique visitors
const trackUniqueVisitor = async (visitorId: string, dates: ReturnType<typeof getDateKey>) => {
  try {
    const visitorRef = doc(db, 'visitors', visitorId);
    const visitorDoc = await getDoc(visitorRef);

    const isNewVisitor = !visitorDoc.exists();
    const lastVisit = visitorDoc.data()?.lastVisit;
    const today = dates.day;
    const isReturnVisitor = lastVisit && lastVisit !== today;

    await setDoc(visitorRef, {
      lastVisit: today,
      visitCount: increment(1),
      firstVisit: isNewVisitor ? today : visitorDoc.data()?.firstVisit
    }, { merge: true });

    // Update unique visitor counts
    if (isNewVisitor) {
      const totalRef = doc(db, 'stats', 'totals');
      await setDoc(totalRef, {
        uniqueVisitors: increment(1)
      }, { merge: true });
    }

    if (isReturnVisitor) {
      const totalRef = doc(db, 'stats', 'totals');
      await setDoc(totalRef, {
        returnVisitors: increment(1)
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error tracking unique visitor:', error);
  }
};

// Get or create a visitor ID stored in localStorage
const getOrCreateVisitorId = (): string => {
  const storageKey = 'prenatal_visitor_id';
  let visitorId = localStorage.getItem(storageKey);

  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(storageKey, visitorId);
  }

  return visitorId;
};

// Track language usage
export const trackLanguageUsage = async (language: string) => {
  if (isAdminSession()) return;
  const dates = getDateKey();

  try {
    // Daily language stats
    const dayLangRef = doc(db, 'stats', 'languages', 'daily', dates.day);
    await setDoc(dayLangRef, {
      [language]: increment(1),
      date: dates.day
    }, { merge: true });

    // Total language stats
    const totalLangRef = doc(db, 'stats', 'languages', 'totals', 'all');
    await setDoc(totalLangRef, {
      [language]: increment(1)
    }, { merge: true });
  } catch (error) {
    console.error('Error tracking language:', error);
  }
};

// Track section views
export const trackSectionView = async (section: string) => {
  if (isAdminSession()) return;
  const dates = getDateKey();

  try {
    // Daily section stats
    const daySectionRef = doc(db, 'stats', 'sections', 'daily', dates.day);
    await setDoc(daySectionRef, {
      [section]: increment(1),
      date: dates.day
    }, { merge: true });

    // Total section stats
    const totalSectionRef = doc(db, 'stats', 'sections', 'totals', 'all');
    await setDoc(totalSectionRef, {
      [section]: increment(1)
    }, { merge: true });
  } catch (error) {
    console.error('Error tracking section:', error);
  }
};

// Track text-to-speech usage
export const trackTextToSpeech = async (section: string, language: string) => {
  if (isAdminSession()) return;
  const dates = getDateKey();

  try {
    const ttsRef = doc(db, 'stats', 'tts', 'daily', dates.day);
    await setDoc(ttsRef, {
      total: increment(1),
      [`${section}_${language}`]: increment(1),
      date: dates.day
    }, { merge: true });

    const totalTtsRef = doc(db, 'stats', 'tts', 'totals', 'all');
    await setDoc(totalTtsRef, {
      total: increment(1),
      [`${section}_${language}`]: increment(1)
    }, { merge: true });
  } catch (error) {
    console.error('Error tracking TTS:', error);
  }
};

// Track session duration
export const trackSessionStart = () => {
  if (isAdminSession()) return;
  sessionStorage.setItem('session_start', Date.now().toString());
};

export const trackSessionEnd = async () => {
  if (isAdminSession()) return;
  const startTime = sessionStorage.getItem('session_start');
  if (startTime) {
    const duration = Math.floor((Date.now() - parseInt(startTime)) / 1000); // in seconds
    // Cap at 30 minutes — anything longer is likely an idle/abandoned tab
    const MAX_SESSION_DURATION = 30 * 60; // 1800 seconds
    if (duration > MAX_SESSION_DURATION || duration < 3) return; // ignore idle tabs and sub-3s bounces
    const dates = getDateKey();

    try {
      const sessionRef = doc(db, 'stats', 'sessions', 'daily', dates.day);
      await setDoc(sessionRef, {
        totalDuration: increment(duration),
        sessionCount: increment(1),
        date: dates.day
      }, { merge: true });
    } catch (error) {
      console.error('Error tracking session:', error);
    }
  }
};

// Get all stats for admin dashboard
export interface StatsData {
  today: { visits: number; date: string };
  thisWeek: { visits: number; week: string };
  thisMonth: { visits: number; month: string };
  totals: {
    totalVisits: number;
    uniqueVisitors: number;
    returnVisitors: number;
  };
  languages: Record<string, number>;
  sections: Record<string, number>;
  ttsUsage: number;
  avgSessionDuration: number;
  recentDays: Array<{ date: string; visits: number }>;
}

export const getStats = async (): Promise<StatsData> => {
  const dates = getDateKey();

  try {
    // Get today's stats
    const dayRef = doc(db, 'stats', 'daily', 'days', dates.day);
    const daySnap = await getDoc(dayRef);
    const today = daySnap.exists()
      ? { visits: daySnap.data().visits || 0, date: dates.day }
      : { visits: 0, date: dates.day };

    // Get this week's stats
    const weekRef = doc(db, 'stats', 'weekly', 'weeks', dates.week);
    const weekSnap = await getDoc(weekRef);
    const thisWeek = weekSnap.exists()
      ? { visits: weekSnap.data().visits || 0, week: dates.week }
      : { visits: 0, week: dates.week };

    // Get this month's stats
    const monthRef = doc(db, 'stats', 'monthly', 'months', dates.month);
    const monthSnap = await getDoc(monthRef);
    const thisMonth = monthSnap.exists()
      ? { visits: monthSnap.data().visits || 0, month: dates.month }
      : { visits: 0, month: dates.month };

    // Get total stats
    const totalRef = doc(db, 'stats', 'totals');
    const totalSnap = await getDoc(totalRef);
    const totals = totalSnap.exists()
      ? {
          totalVisits: totalSnap.data().totalVisits || 0,
          uniqueVisitors: totalSnap.data().uniqueVisitors || 0,
          returnVisitors: totalSnap.data().returnVisitors || 0
        }
      : { totalVisits: 0, uniqueVisitors: 0, returnVisitors: 0 };

    // Get language stats
    const langRef = doc(db, 'stats', 'languages', 'totals', 'all');
    const langSnap = await getDoc(langRef);
    const languages = langSnap.exists() ? langSnap.data() : {};

    // Get section stats
    const sectionRef = doc(db, 'stats', 'sections', 'totals', 'all');
    const sectionSnap = await getDoc(sectionRef);
    const sections = sectionSnap.exists() ? sectionSnap.data() : {};

    // Get TTS stats
    const ttsRef = doc(db, 'stats', 'tts', 'totals', 'all');
    const ttsSnap = await getDoc(ttsRef);
    const ttsUsage = ttsSnap.exists() ? (ttsSnap.data().total || 0) : 0;

    // Get session stats for average duration
    const sessionRef = doc(db, 'stats', 'sessions', 'daily', dates.day);
    const sessionSnap = await getDoc(sessionRef);
    let avgSessionDuration = 0;
    if (sessionSnap.exists()) {
      const data = sessionSnap.data();
      if (data.sessionCount > 0) {
        avgSessionDuration = Math.floor(data.totalDuration / data.sessionCount);
      }
    }

    // Get recent days data
    const daysCollection = collection(db, 'stats', 'daily', 'days');
    const recentQuery = query(daysCollection, orderBy('date', 'desc'), limit(7));
    const recentSnap = await getDocs(recentQuery);
    const recentDays = recentSnap.docs.map(doc => ({
      date: doc.data().date,
      visits: doc.data().visits || 0
    }));

    return {
      today,
      thisWeek,
      thisMonth,
      totals,
      languages,
      sections,
      ttsUsage,
      avgSessionDuration,
      recentDays
    };
  } catch (error) {
    console.error('Error getting stats:', error);
    return {
      today: { visits: 0, date: dates.day },
      thisWeek: { visits: 0, week: dates.week },
      thisMonth: { visits: 0, month: dates.month },
      totals: { totalVisits: 0, uniqueVisitors: 0, returnVisitors: 0 },
      languages: {},
      sections: {},
      ttsUsage: 0,
      avgSessionDuration: 0,
      recentDays: []
    };
  }
};

// Clear all stats data
export const clearAllStats = async (): Promise<void> => {
  try {
    // Helper to delete all docs in a subcollection
    const deleteSubcollection = async (parentPath: string, subcollectionName: string) => {
      const colRef = collection(db, parentPath, subcollectionName);
      const snapshot = await getDocs(colRef);
      const deletePromises = snapshot.docs.map(d => deleteDoc(d.ref));
      await Promise.all(deletePromises);
    };

    // Delete daily stats
    await deleteSubcollection('stats/daily', 'days');

    // Delete weekly stats
    await deleteSubcollection('stats/weekly', 'weeks');

    // Delete monthly stats
    await deleteSubcollection('stats/monthly', 'months');

    // Delete language stats
    await deleteSubcollection('stats/languages', 'daily');
    await deleteSubcollection('stats/languages', 'totals');

    // Delete section stats
    await deleteSubcollection('stats/sections', 'daily');
    await deleteSubcollection('stats/sections', 'totals');

    // Delete TTS stats
    await deleteSubcollection('stats/tts', 'daily');
    await deleteSubcollection('stats/tts', 'totals');

    // Delete session stats
    await deleteSubcollection('stats/sessions', 'daily');

    // Reset totals document
    await setDoc(doc(db, 'stats', 'totals'), {
      totalVisits: 0,
      uniqueVisitors: 0,
      returnVisitors: 0
    });

    // Delete all visitor documents
    const visitorsRef = collection(db, 'visitors');
    const visitorsSnap = await getDocs(visitorsRef);
    const visitorDeletes = visitorsSnap.docs.map(d => deleteDoc(d.ref));
    await Promise.all(visitorDeletes);
  } catch (error) {
    console.error('Error clearing stats:', error);
    throw error;
  }
};

// Date range types
export type DateRangePreset = 'today' | 'last7days' | 'last30days' | 'thisMonth' | 'custom';

export interface DateRangeStatsData {
  startDate: string;
  endDate: string;
  totalVisits: number;
  dailyVisits: Array<{ date: string; visits: number }>;
  languages: Record<string, number>;
  sections: Record<string, number>;
  ttsUsage: number;
  ttsBreakdown: Record<string, number>;
  avgSessionDuration: number;
  totalSessions: number;
  allTimeTotals: {
    totalVisits: number;
    uniqueVisitors: number;
    returnVisitors: number;
  };
}

// Get date range for a preset
export const getDateRangeForPreset = (preset: DateRangePreset): { startDate: string; endDate: string } => {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0];

  switch (preset) {
    case 'today':
      return { startDate: endDate, endDate };
    case 'last7days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { startDate: start.toISOString().split('T')[0], endDate };
    }
    case 'last30days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return { startDate: start.toISOString().split('T')[0], endDate };
    }
    case 'thisMonth': {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: startOfMonth.toISOString().split('T')[0], endDate };
    }
    default:
      return { startDate: endDate, endDate };
  }
};

// Get stats for a specific date range
export const getStatsForDateRange = async (
  startDate: string,
  endDate: string
): Promise<DateRangeStatsData> => {
  try {
    // Query daily visits in range
    const daysCollection = collection(db, 'stats', 'daily', 'days');
    const daysQuery = query(
      daysCollection,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc')
    );
    const daysSnap = await getDocs(daysQuery);
    const dailyVisits = daysSnap.docs.map(d => ({
      date: d.data().date as string,
      visits: (d.data().visits as number) || 0
    }));
    const totalVisits = dailyVisits.reduce((sum, d) => sum + d.visits, 0);

    // Query language usage in range
    const langCollection = collection(db, 'stats', 'languages', 'daily');
    const langQuery = query(
      langCollection,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const langSnap = await getDocs(langQuery);
    const languages: Record<string, number> = {};
    langSnap.docs.forEach(d => {
      const data = d.data();
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'date' && typeof value === 'number') {
          languages[key] = (languages[key] || 0) + value;
        }
      });
    });

    // Query section views in range
    const sectionCollection = collection(db, 'stats', 'sections', 'daily');
    const sectionQuery = query(
      sectionCollection,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const sectionSnap = await getDocs(sectionQuery);
    const sections: Record<string, number> = {};
    sectionSnap.docs.forEach(d => {
      const data = d.data();
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'date' && typeof value === 'number') {
          sections[key] = (sections[key] || 0) + value;
        }
      });
    });

    // Query TTS usage in range
    const ttsCollection = collection(db, 'stats', 'tts', 'daily');
    const ttsQuery = query(
      ttsCollection,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const ttsSnap = await getDocs(ttsQuery);
    let ttsUsage = 0;
    const ttsBreakdown: Record<string, number> = {};
    ttsSnap.docs.forEach(d => {
      const data = d.data();
      ttsUsage += (data.total as number) || 0;
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'date' && key !== 'total' && typeof value === 'number') {
          ttsBreakdown[key] = (ttsBreakdown[key] || 0) + value;
        }
      });
    });

    // Query session data in range
    const sessionCollection = collection(db, 'stats', 'sessions', 'daily');
    const sessionQuery = query(
      sessionCollection,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const sessionSnap = await getDocs(sessionQuery);
    let totalDuration = 0;
    let totalSessions = 0;
    const MAX_AVG_PER_DAY = 30 * 60; // 30 minutes cap per day's average
    sessionSnap.docs.forEach(d => {
      const dayDuration = (d.data().totalDuration as number) || 0;
      const daySessions = (d.data().sessionCount as number) || 0;
      if (daySessions > 0) {
        const dayAvg = dayDuration / daySessions;
        // If a day's average exceeds 30 min, cap it (bad data from idle tabs)
        const cappedDuration = dayAvg > MAX_AVG_PER_DAY ? MAX_AVG_PER_DAY * daySessions : dayDuration;
        totalDuration += cappedDuration;
        totalSessions += daySessions;
      }
    });
    const avgSessionDuration = totalSessions > 0 ? Math.floor(totalDuration / totalSessions) : 0;

    // Always fetch all-time totals
    const totalRef = doc(db, 'stats', 'totals');
    const totalSnap = await getDoc(totalRef);
    const allTimeTotals = totalSnap.exists()
      ? {
          totalVisits: totalSnap.data().totalVisits || 0,
          uniqueVisitors: totalSnap.data().uniqueVisitors || 0,
          returnVisitors: totalSnap.data().returnVisitors || 0
        }
      : { totalVisits: 0, uniqueVisitors: 0, returnVisitors: 0 };

    return {
      startDate,
      endDate,
      totalVisits,
      dailyVisits,
      languages,
      sections,
      ttsUsage,
      ttsBreakdown,
      avgSessionDuration,
      totalSessions,
      allTimeTotals
    };
  } catch (error) {
    console.error('Error getting stats for date range:', error);
    return {
      startDate,
      endDate,
      totalVisits: 0,
      dailyVisits: [],
      languages: {},
      sections: {},
      ttsUsage: 0,
      ttsBreakdown: {},
      avgSessionDuration: 0,
      totalSessions: 0,
      allTimeTotals: { totalVisits: 0, uniqueVisitors: 0, returnVisitors: 0 }
    };
  }
};

// Compute the previous period's date range for comparison
export const getPreviousPeriodRange = (startDate: string, endDate: string): { startDate: string; endDate: string } => {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const durationMs = end.getTime() - start.getTime() + 24 * 60 * 60 * 1000; // inclusive
  const prevEnd = new Date(start.getTime() - 24 * 60 * 60 * 1000); // day before current start
  const prevStart = new Date(prevEnd.getTime() - durationMs + 24 * 60 * 60 * 1000);
  return {
    startDate: prevStart.toISOString().split('T')[0],
    endDate: prevEnd.toISOString().split('T')[0]
  };
};
